import React from 'react';
import { motion } from 'framer-motion';

// 辅助函数：生成金属光泽颜色
const getMetallicColors = (baseColor) => {
  // 将十六进制颜色转换为RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // 计算高光色（增加亮度）
  const highlightR = Math.min(255, r + 80);
  const highlightG = Math.min(255, g + 80);
  const highlightB = Math.min(255, b + 80);
  
  // 计算阴影色（降低亮度）
  const shadowR = Math.max(0, r - 60);
  const shadowG = Math.max(0, g - 60);
  const shadowB = Math.max(0, b - 60);
  
  // 转换为十六进制
  const toHex = (n) => n.toString(16).padStart(2, '0');
  const highlight = `#${toHex(highlightR)}${toHex(highlightG)}${toHex(highlightB)}`;
  const shadow = `#${toHex(shadowR)}${toHex(shadowG)}${toHex(shadowB)}`;
  
  return { highlight, base: baseColor, shadow };
};

// 通用辅助组件：定义渐变和滤镜（金属光泽版本）
const IconDefs = ({ id, color, glow = true }) => {
  const metallic = getMetallicColors(color);
  
  return (
    <defs>
      {/* 金属光泽渐变 */}
      <linearGradient id={`metallic-${id}`} x1="0%" y1="0%" x2="0%" y2="100%" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={metallic.highlight} stopOpacity="1" />
        <stop offset="30%" stopColor={metallic.highlight} stopOpacity="0.9" />
        <stop offset="50%" stopColor={metallic.base} stopOpacity="1" />
        <stop offset="70%" stopColor={metallic.base} stopOpacity="0.9" />
        <stop offset="100%" stopColor={metallic.shadow} stopOpacity="0.8" />
      </linearGradient>
      {/* 原有的渐变（保留兼容性） */}
      <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={color} stopOpacity="0.8" />
        <stop offset="50%" stopColor={color} stopOpacity="0.4" />
        <stop offset="100%" stopColor={color} stopOpacity="0.1" />
      </linearGradient>
      {glow ? (
        <filter id={`glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      ) : null}
    </defs>
  );
};

// 1. 一线互联网：抽象六边形网络 (Abstract Hex Network)
export const AdvancedNetwork = ({ color = "#2563EB", size = 24, animated = true, glow = true }) => {
  const id = `network-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 中心核心 */}
        {animated ? (
          <motion.circle
            cx="12" cy="12" r="3"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
        ) : (
          <circle cx="12" cy="12" r="3" fill={metallicFill} filter={glowFilter} opacity="0.9" />
        )}
        {/* 旋转的轨道和卫星 */}
        {animated ? (
          <>
            <motion.g animate={{ rotate: 360 }} transition={{ duration: 10, repeat: Infinity, ease: "linear" }}>
              <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1" strokeDasharray="2 4" strokeOpacity="0.5" />
              <circle cx="20" cy="12" r="1.5" fill={metallicFill} />
              <circle cx="4" cy="12" r="1.5" fill={metallicFill} />
            </motion.g>
            <motion.g animate={{ rotate: -360 }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }}>
              <circle cx="12" cy="12" r="5.5" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
              <circle cx="12" cy="6.5" r="1" fill={metallicFill} />
              <circle cx="12" cy="17.5" r="1" fill={metallicFill} />
            </motion.g>
          </>
        ) : (
          <>
            <g>
              <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1" strokeDasharray="2 4" strokeOpacity="0.35" />
              <circle cx="20" cy="12" r="1.5" fill={metallicFill} opacity="0.9" />
              <circle cx="4" cy="12" r="1.5" fill={metallicFill} opacity="0.9" />
            </g>
            <g>
              <circle cx="12" cy="12" r="5.5" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" />
              <circle cx="12" cy="6.5" r="1" fill={metallicFill} opacity="0.9" />
              <circle cx="12" cy="17.5" r="1" fill={metallicFill} opacity="0.9" />
            </g>
          </>
        )}
        {/* 连接线 */}
        <path d="M12 12L20 12M12 12L4 12M12 12L12 6.5" stroke={color} strokeWidth="0.5" strokeOpacity="0.2" />
      </svg>
    </div>
  );
};

// 2. 头部科技公司：抽象芯片 (Advanced Tech Chip)
export const AdvancedTechChip = ({ color = "#7C3AED", size = 24, animated = true, glow = true }) => {
  const baseColor = color || "#7C3AED";
  const id = `tech-${baseColor.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={baseColor} glow={glow} />
        
        {/* 背景光晕 */}
        <rect x="4" y="4" width="16" height="16" rx="4" fill={`url(#gradient-${id})`} filter={glowFilter} opacity="0.3"/>

        {/* 芯片主体 */}
        {animated ? (
          <motion.rect
            x="5" y="5" width="14" height="14" rx="3" stroke={baseColor} strokeWidth="1.5" fill="none"
            animate={{ strokeOpacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <rect x="5" y="5" width="14" height="14" rx="3" stroke={baseColor} strokeWidth="1.5" fill="none" opacity="0.9" />
        )}

        {/* 内部核心 */}
        {animated ? (
          <motion.g style={{ transformOrigin: "12px 12px" }} animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
            <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill={metallicFill} fillOpacity="0.8" />
          </motion.g>
        ) : (
          <g>
            <rect x="9.5" y="9.5" width="5" height="5" rx="1" fill={metallicFill} fillOpacity="0.8" />
          </g>
        )}

        {/* 电路连接线 */}
        <g stroke={baseColor} strokeWidth="1.5" strokeLinecap="round">
          {animated ? (
            <motion.path
              d="M9 5V2 M15 5V2 M9 19V22 M15 19V22 M5 9H2 M5 15H2 M19 9H22 M19 15H22"
              animate={{ opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          ) : (
            <path d="M9 5V2 M15 5V2 M9 19V22 M15 19V22 M5 9H2 M5 15H2 M19 9H22 M19 15H22" opacity="0.55" />
          )}
        </g>
      </svg>
    </div>
  );
};

// 3. 大型银行 / 金融：银行大厦 (Bank Building)
export const AdvancedFinance = ({ color = "#0891B2", size = 24, animated = true, glow = true }) => {
  const id = `finance-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 地基 */}
        <rect x="2" y="20" width="20" height="2" rx="0.5" fill={metallicFill} fillOpacity="0.5" />
        {/* 主建筑 */}
        {animated ? (
          <motion.rect
            x="6"
            y="6"
            width="12"
            height="14"
            rx="1"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          />
        ) : (
          <rect x="6" y="6" width="12" height="14" rx="1" fill={metallicFill} filter={glowFilter} opacity="0.9" />
        )}
        {/* 经典银行柱式结构 */}
        <rect x="7" y="6" width="2" height="14" fill={metallicFill} fillOpacity="0.3" />
        <rect x="15" y="6" width="2" height="14" fill={metallicFill} fillOpacity="0.3" />
        {/* 三角形屋顶/门楣 */}
        <path d="M6 6L12 2L18 6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill={metallicFill} fillOpacity="0.6" />
        {/* 中心货币符号 */}
        {animated ? (
          <motion.circle
            cx="12"
            cy="13"
            r="3.5"
            stroke={color}
            strokeWidth="1.5"
            fill="none"
            strokeOpacity="0.6"
            animate={{ scale: [1, 1.1, 1], opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        ) : (
          <circle cx="12" cy="13" r="3.5" stroke={color} strokeWidth="1.5" fill="none" strokeOpacity="0.7" />
        )}
        {/* 货币符号 - 美元符号 */}
        <path d="M12 10.5V15.5M10.5 12H13.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M12 9.5C11.5 9.5 11 9.8 11 10.2C11 10.6 11.4 11 12 11C12.6 11 13 11.4 13 11.8C13 12.2 12.5 12.5 12 12.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" />
        <path d="M12 15.5C12.5 15.5 13 15.2 13 14.8C13 14.4 12.6 14 12 14C11.4 14 11 13.6 11 13.2C11 12.8 11.5 12.5 12 12.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" />
      </svg>
    </div>
  );
};

// 4. 独角兽 / 高成长：光速火箭 (Hyper Rocket)
export const AdvancedGrowth = ({ color = "#DB2777", size = 24, animated = true, glow = true }) => {
  const id = `growth-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 尾迹 */}
        <path d="M6 21C6 21 10 18 12 12" stroke={color} strokeWidth="2" strokeOpacity="0.25" strokeLinecap="round" />
        <path d="M18 21C18 21 14 18 12 12" stroke={color} strokeWidth="2" strokeOpacity="0.25" strokeLinecap="round" />
        {/* 火箭主体 */}
        {animated ? (
          <motion.path
            d="M12 2L15 8L12 22L9 8L12 2Z"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ y: [-2, 2, -2] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <path d="M12 2L15 8L12 22L9 8L12 2Z" fill={metallicFill} filter={glowFilter} />
        )}
        {/* 推进光效 */}
        {animated ? (
          <motion.circle cx="12" cy="22" r="2" fill="white" animate={{ opacity: [0, 1, 0], scale: [0.5, 1.5, 0.5] }} transition={{ duration: 0.5, repeat: Infinity }} />
        ) : (
          <circle cx="12" cy="22" r="1.8" fill="white" opacity="0.8" />
        )}
      </svg>
    </div>
  );
};

// 5. 外企 / 跨国：全息地球 (Holographic Globe)
export const AdvancedGlobe = ({ color = "#059669", size = 24, animated = true, glow = true }) => {
  const id = `globe-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1" strokeOpacity="0.3" />
        {/* 经线 */}
        {animated ? (
          <>
            <motion.ellipse cx="12" cy="12" rx="4" ry="10" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" animate={{ rx: [4, 0, 4] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} />
            <motion.ellipse cx="12" cy="12" rx="8" ry="10" stroke={color} strokeWidth="1.5" strokeOpacity="0.4" animate={{ rx: [8, 2, 8] }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} />
          </>
        ) : (
          <>
            <ellipse cx="12" cy="12" rx="4" ry="10" stroke={color} strokeWidth="1.5" strokeOpacity="0.55" />
            <ellipse cx="12" cy="12" rx="8" ry="10" stroke={color} strokeWidth="1.5" strokeOpacity="0.35" />
          </>
        )}
        {/* 纬线 */}
        <path d="M2 12H22" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
        <path d="M4 7H20" stroke={color} strokeWidth="1" strokeOpacity="0.3" />
        <path d="M4 17H20" stroke={color} strokeWidth="1" strokeOpacity="0.3" />
        {/* 核心光点 */}
        <circle cx="12" cy="12" r="2" fill={metallicFill} filter={glowFilter} />
      </svg>
    </div>
  );
};

// 6. 国企 / 央企：机构大厦 (Institutional Building)
export const AdvancedBadge = ({ color = "#D97706", size = 24, animated = true, glow = true }) => {
  const id = `badge-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 地基 */}
        <rect x="2" y="20" width="20" height="2" rx="0.5" fill={metallicFill} fillOpacity="0.6" />
        {/* 左侧建筑 */}
        {animated ? (
          <motion.rect
            x="3"
            y="8"
            width="6"
            height="12"
            rx="1"
            fill={metallicFill}
            fillOpacity="0.7"
            filter={glowFilter}
            animate={{ opacity: [0.7, 0.9, 0.7] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          />
        ) : (
          <rect x="3" y="8" width="6" height="12" rx="1" fill={metallicFill} fillOpacity="0.75" filter={glowFilter} />
        )}
        {/* 中间主建筑 */}
        {animated ? (
          <motion.rect
            x="10"
            y="4"
            width="4"
            height="16"
            rx="1"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        ) : (
          <rect x="10" y="4" width="4" height="16" rx="1" fill={metallicFill} filter={glowFilter} opacity="0.9" />
        )}
        {/* 右侧建筑 */}
        {animated ? (
          <motion.rect
            x="15"
            y="10"
            width="6"
            height="10"
            rx="1"
            fill={metallicFill}
            fillOpacity="0.7"
            filter={glowFilter}
            animate={{ opacity: [0.7, 0.9, 0.7] }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
          />
        ) : (
          <rect x="15" y="10" width="6" height="10" rx="1" fill={metallicFill} fillOpacity="0.75" filter={glowFilter} />
        )}
        {/* 窗户细节 */}
        <g stroke={color} strokeWidth="0.8" strokeOpacity="0.4">
          {/* 左侧窗户 */}
          <line x1="4.5" y1="11" x2="4.5" y2="13" />
          <line x1="7.5" y1="11" x2="7.5" y2="13" />
          <line x1="4.5" y1="15" x2="4.5" y2="17" />
          <line x1="7.5" y1="15" x2="7.5" y2="17" />
          {/* 中间窗户 */}
          <line x1="11" y1="7" x2="11" y2="9" />
          <line x1="13" y1="7" x2="13" y2="9" />
          <line x1="11" y1="11" x2="11" y2="13" />
          <line x1="13" y1="11" x2="13" y2="13" />
          <line x1="11" y1="15" x2="11" y2="17" />
          <line x1="13" y1="15" x2="13" y2="17" />
          {/* 右侧窗户 */}
          <line x1="16.5" y1="13" x2="16.5" y2="15" />
          <line x1="19.5" y1="13" x2="19.5" y2="15" />
          <line x1="16.5" y1="17" x2="16.5" y2="19" />
          <line x1="19.5" y1="17" x2="19.5" y2="19" />
        </g>
        {/* 顶部装饰/旗帜 */}
        {animated ? (
          <motion.circle
            cx="12"
            cy="4"
            r="1.5"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        ) : (
          <circle cx="12" cy="4" r="1.5" fill={metallicFill} filter={glowFilter} opacity="0.9" />
        )}
      </svg>
    </div>
  );
};

// 7. 初创公司：能量迸发 (Energy Burst)
export const AdvancedStartup = ({ color = "#EA580C", size = 24, animated = true, glow = true }) => {
  const id = `startup-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {animated ? (
          <motion.g
            style={{ transformOrigin: "12px 14px" }}
            animate={{ rotate: [-2, 2, -2] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.ellipse
              cx="12"
              cy="18"
              rx="3.2"
              ry="2.2"
              fill={metallicFill}
              filter={glowFilter}
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <path d="M12 17C12 14.8 12 13.4 12 11" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
            <motion.path
              d="M12 12C9.5 11.8 8.2 10.4 7 8.4C9.8 8.2 11.4 9.2 12 11"
              fill="none"
              stroke={color}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ pathLength: [0.95, 1, 0.95] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.path
              d="M12 11C14.6 10.8 16 9.4 17 7.4C14.2 7.2 12.6 8.2 12 10.2"
              fill="none"
              stroke={color}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ pathLength: [0.95, 1, 0.95] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
            />
            <circle cx="12" cy="10.2" r="1" fill={metallicFill} opacity="0.95" />
          </motion.g>
        ) : (
          <g>
            <ellipse cx="12" cy="18" rx="3.2" ry="2.2" fill={metallicFill} filter={glowFilter} opacity="0.95" />
            <path d="M12 17C12 14.8 12 13.4 12 11" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
            <path
              d="M12 12C9.5 11.8 8.2 10.4 7 8.4C9.8 8.2 11.4 9.2 12 11"
              fill="none"
              stroke={color}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 11C14.6 10.8 16 9.4 17 7.4C14.2 7.2 12.6 8.2 12 10.2"
              fill="none"
              stroke={color}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="10.2" r="1" fill={metallicFill} opacity="0.95" />
          </g>
        )}
      </svg>
    </div>
  );
};

// 8. 咨询 / 专业服务：专业握手 (Professional Handshake)
export const AdvancedService = ({ color = "#4F46E5", size = 24, animated = true, glow = true }) => {
  const id = `service-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 左侧手臂 */}
        <path d="M5 14C5 12 6 10 8 10C9 10 10 10.5 10.5 11.5C10.8 12 11 12.5 11 13V16C11 17 10 18 9 18C8 18 7 17 7 16V14Z" fill={metallicFill} fillOpacity="0.6" />
        {/* 右侧手臂 */}
        <path d="M19 14C19 12 18 10 16 10C15 10 14 10.5 13.5 11.5C13.2 12 13 12.5 13 13V16C13 17 14 18 15 18C16 18 17 17 17 16V14Z" fill={metallicFill} fillOpacity="0.6" />
        {/* 握手中心 */}
        {animated ? (
          <motion.g
            style={{ transformOrigin: "12px 12px" }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <circle cx="12" cy="12" r="3.5" fill={metallicFill} filter={glowFilter} opacity="0.8" />
            <path d="M10 11L12 13L14 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <path d="M10 13L12 11L14 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </motion.g>
        ) : (
          <g>
            <circle cx="12" cy="12" r="3.5" fill={metallicFill} filter={glowFilter} opacity="0.8" />
            <path d="M10 11L12 13L14 11" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <path d="M10 13L12 11L14 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </g>
        )}
        {/* 专业文档背景 */}
        <rect x="3" y="3" width="6" height="4" rx="0.5" fill={metallicFill} fillOpacity="0.2" />
        <rect x="15" y="17" width="6" height="4" rx="0.5" fill={metallicFill} fillOpacity="0.2" />
      </svg>
    </div>
  );
};

// 9. 电商 / 零售：购物袋 (Shopping Bag)
export const AdvancedRetail = ({ color = "#DC2626", size = 24, animated = true, glow = true }) => {
  const id = `retail-${color.replace('#', '')}`;
  const glowFilter = glow ? `url(#glow-${id})` : undefined;
  const metallicFill = `url(#metallic-${id})`;
  return (
    <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ overflow: 'visible' }}>
        <IconDefs id={id} color={color} glow={glow} />
        {/* 购物袋主体 */}
        {animated ? (
          <motion.path
            d="M7 8H17L16 20H8L7 8Z"
            fill={metallicFill}
            filter={glowFilter}
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <path d="M7 8H17L16 20H8L7 8Z" fill={metallicFill} filter={glowFilter} opacity="0.9" />
        )}
        {/* 购物袋提手 */}
        <path d="M9 8C9 6.5 10 5.5 11.5 5.5C13 5.5 14 6.5 14 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
        {/* 购物袋底部 */}
        <path d="M7 20H17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.6" />
        {/* 购物袋内部物品 */}
        {animated ? (
          <>
            <motion.circle
              cx="10"
              cy="13"
              r="1.5"
              fill="white"
              fillOpacity="0.8"
              animate={{ y: [13, 12, 13] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx="14"
              cy="15"
              r="1.2"
              fill="white"
              fillOpacity="0.8"
              animate={{ y: [15, 14, 15] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
            />
            <motion.rect
              x="11.5"
              y="11"
              width="1.5"
              height="2"
              rx="0.3"
              fill="white"
              fillOpacity="0.8"
              animate={{ y: [11, 10.5, 11] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            />
          </>
        ) : (
          <>
            <circle cx="10" cy="13" r="1.5" fill="white" fillOpacity="0.7" />
            <circle cx="14" cy="15" r="1.2" fill="white" fillOpacity="0.7" />
            <rect x="11.5" y="11" width="1.5" height="2" rx="0.3" fill="white" fillOpacity="0.7" />
          </>
        )}
        {/* 价格标签 */}
        <path d="M17 6C17 5 16.5 4 15.5 4C14.5 4 14 5 14 6" stroke={color} strokeWidth="1" strokeLinecap="round" fill="none" strokeOpacity="0.6" />
      </svg>
    </div>
  );
};
